import { useState } from 'react'
import { motion } from 'framer-motion'
import { ShieldAlert, ChevronRight, SendIcon, TicketIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import type { InteractionEvent } from '@/lib/supportApi'

interface InteractionCardProps {
  interaction: InteractionEvent
  onRespond: (value: string) => void
}

export function InteractionCard({ interaction, onRespond }: InteractionCardProps) {
  switch (interaction.interaction_type) {
    case 'single_select':
      return <ProductPoll interaction={interaction} onSelect={onRespond} />
    case 'confirm':
      return <ConfirmationPrompt interaction={interaction} onRespond={onRespond} />
    case 'text_input':
      return <TextInputPrompt interaction={interaction} onSubmit={onRespond} />
    default:
      return null
  }
}

function ProductPoll({
  interaction,
  onSelect,
}: {
  interaction: InteractionEvent
  onSelect: (value: string) => void
}) {
  const options = interaction.options ?? []
  if (options.length === 0) return null

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="ml-9 space-y-2"
    >
      {options.map((option, idx) => (
        <button
          key={option.id}
          onClick={() => onSelect(option.label)}
          className="w-full flex items-center gap-3 rounded-lg border border-border/60 px-4 py-3 text-left transition-colors hover:border-primary/40 hover:bg-primary/5 group"
        >
          <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-border/80 text-xs font-medium text-muted-foreground group-hover:border-primary/50 group-hover:text-primary transition-colors">
            {idx + 1}
          </span>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-foreground truncate">
              {option.label}
            </p>
            {option.description && (
              <p className="text-xs text-muted-foreground mt-0.5 truncate">
                {option.description}
              </p>
            )}
          </div>
          <ChevronRight className="h-4 w-4 text-muted-foreground/50 shrink-0 group-hover:text-primary/60 transition-colors" />
        </button>
      ))}
    </motion.div>
  )
}

function ConfirmationPrompt({
  interaction,
  onRespond,
}: {
  interaction: InteractionEvent
  onRespond: (value: string) => void
}) {
  const details = interaction.confirm_details ?? {}
  const existingTicket = interaction.context_data?.existing_ticket as
    | Record<string, unknown>
    | undefined

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="ml-9 space-y-2"
    >
      {existingTicket && (
        <div className="rounded-lg border border-blue-500/30 bg-blue-500/5 px-4 py-3">
          <div className="flex items-center gap-2">
            <TicketIcon className="h-4 w-4 text-blue-500 shrink-0" />
            <p className="text-xs font-medium text-foreground">
              Ticket #{String(existingTicket.number ?? '?')} em aberto
            </p>
            <Badge
              variant="outline"
              className="text-[10px] border-blue-500/40 text-blue-500 bg-blue-500/10"
            >
              {String(existingTicket.status ?? 'aberto')}
            </Badge>
          </div>
        </div>
      )}

      <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 px-4 py-3">
        <div className="flex items-start gap-2.5">
          <ShieldAlert className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-xs font-medium text-foreground">
              {interaction.confirm_action ?? interaction.title}
            </p>
            {interaction.message && (
              <p className="text-xs text-muted-foreground mt-1">
                {interaction.message}
              </p>
            )}
            {Object.keys(details).length > 0 && (
              <div className="mt-1.5 space-y-0.5">
                {Object.entries(details).map(([key, value]) => (
                  <p key={key} className="text-[11px] text-muted-foreground">
                    <span className="font-medium">{key}:</span> {String(value)}
                  </p>
                ))}
              </div>
            )}
            <div className="flex gap-2 mt-3">
              <Button
                size="sm"
                variant="default"
                className="h-7 px-3 text-xs"
                onClick={() => onRespond('Sim')}
              >
                Sim, pode criar
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="h-7 px-3 text-xs"
                onClick={() => onRespond('Não')}
              >
                Não precisa
              </Button>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  )
}

function TextInputPrompt({
  interaction,
  onSubmit,
}: {
  interaction: InteractionEvent
  onSubmit: (value: string) => void
}) {
  const [value, setValue] = useState('')
  const [error, setError] = useState('')

  const handleSubmit = () => {
    const trimmed = value.trim()
    if (!trimmed) return

    if (interaction.placeholder?.includes('@') && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setError('Por favor, informe um e-mail válido.')
      return
    }

    setError('')
    onSubmit(trimmed)
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="ml-9 flex flex-col gap-2"
    >
      <div className="flex gap-2">
        <Input
          type={interaction.placeholder?.includes('@') ? 'email' : 'text'}
          placeholder={interaction.placeholder ?? 'Digite aqui...'}
          value={value}
          onChange={(e) => {
            setValue(e.target.value)
            setError('')
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              handleSubmit()
            }
          }}
          className="flex-1 h-9 text-sm"
          autoFocus
        />
        <Button
          size="sm"
          className="h-9 px-4"
          onClick={handleSubmit}
          disabled={!value.trim()}
        >
          <SendIcon className="h-3.5 w-3.5" />
        </Button>
      </div>
      {error && <p className="text-xs text-destructive">{error}</p>}
    </motion.div>
  )
}
